/**
 * DΛST Widget Component
 *
 * Dynamic Lambda Symbol Tracker sidebar widget with real-time context updates
 * Displays symbolic tags, context scores, and lambda fingerprints
 */

import React, { useState, useEffect, useRef } from 'react'
import { Hash, Activity, TrendingUp, Search, Plus, X, AlertCircle, Check, Zap } from 'lucide-react'
import { DASTAPI, ContextSnapshot } from '../../lib/api-client'

interface DASTWidgetProps {
  userId: string
  position?: 'left' | 'right'
  onClose?: () => void
  compact?: boolean
  onVariableSearch?: (variable: string) => void
}

type SymbolCategory =
  | 'ACTIVITY'
  | 'CONTEXT'
  | 'MOOD'
  | 'FOCUS'
  | 'GOAL'
  | 'RELATIONSHIP'
  | 'TEMPORAL'
  | 'CREATIVE'
  | 'LEARNING'
  | 'WELLNESS'

export const DASTWidget: React.FC<DASTWidgetProps> = ({
  userId,
  position = 'right',
  onClose,
  compact = false,
  onVariableSearch,
}) => {
  const [contextSnapshot, setContextSnapshot] = useState<ContextSnapshot | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<SymbolCategory | 'all'>('all')
  const [isAddingSymbol, setIsAddingSymbol] = useState(false)
  const [newSymbol, setNewSymbol] = useState('')
  const [newSymbolCategory, setNewSymbolCategory] = useState<SymbolCategory>('ACTIVITY')
  const [isConnected, setIsConnected] = useState(false)
  const [connectionError, setConnectionError] = useState<string | null>(null)
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const eventSourceRef = useRef<EventSource | null>(null)

  // Categories configuration
  const categories: SymbolCategory[] = [
    'ACTIVITY',
    'CONTEXT',
    'MOOD',
    'FOCUS',
    'GOAL',
    'RELATIONSHIP',
    'TEMPORAL',
    'CREATIVE',
    'LEARNING',
    'WELLNESS',
  ]

  // Initialize SSE stream for context updates
  useEffect(() => {
    const connectStream = () => {
      try {
        const eventSource = DASTAPI.createContextStream(userId)
        eventSourceRef.current = eventSource

        eventSource.onopen = () => {
          setIsConnected(true)
          setConnectionError(null)
        }

        eventSource.onmessage = (event) => {
          const data = JSON.parse(event.data)

          if (data.type === 'context_snapshot' && data.snapshot) {
            const snapshot: ContextSnapshot = {
              user_id: data.snapshot.user_id,
              tags: data.snapshot.tags || [],
              primary_activity: data.snapshot.primary_activity,
              focus_score: data.snapshot.focus_score || 0,
              coherence_score: data.snapshot.coherence_score || 0,
              stability_score: data.snapshot.stability_score || 0,
              lambda_fingerprint: data.snapshot.lambda_fingerprint,
              timestamp: data.snapshot.timestamp,
            }
            setContextSnapshot(snapshot)
          }
        }

        eventSource.onerror = (error) => {
          console.error('DΛST SSE error:', error)
          setIsConnected(false)
          setConnectionError('Connection lost. Reconnecting...')
          eventSource.close()

          // Attempt reconnection after 5 seconds
          setTimeout(() => connectStream(), 5000)
        }
      } catch (error) {
        console.error('Failed to create DΛST stream:', error)
        setConnectionError('Failed to connect to DΛST service')
      }
    }

    // Initial context fetch
    const fetchInitialContext = async () => {
      try {
        const snapshot = await DASTAPI.getContextSnapshot(userId)
        setContextSnapshot(snapshot)
      } catch (error) {
        console.error('Failed to fetch initial context:', error)
      }
    }

    fetchInitialContext()
    connectStream()

    return () => {
      eventSourceRef.current?.close()
    }
  }, [userId])

  // Show notification
  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message })
    setTimeout(() => setNotification(null), 3000)
  }

  // Add symbol
  const handleAddSymbol = async () => {
    if (!newSymbol.trim()) return

    try {
      await DASTAPI.addSymbol(userId, newSymbol.trim(), newSymbolCategory, {
        source: 'USER_EXPLICIT',
        confidence: 1.0,
      })
      showNotification('success', `Symbol "${newSymbol}" added`)
      setNewSymbol('')
      setIsAddingSymbol(false)

      // Refresh context
      const snapshot = await DASTAPI.getContextSnapshot(userId)
      setContextSnapshot(snapshot)
    } catch (error) {
      console.error('Failed to add symbol:', error)
      showNotification('error', 'Failed to add symbol')
    }
  }

  // Remove symbol
  const handleRemoveSymbol = async (symbol: string, category: string) => {
    try {
      await DASTAPI.removeSymbol(userId, symbol, category)
      showNotification('success', `Symbol "${symbol}" removed`)

      // Refresh context
      const snapshot = await DASTAPI.getContextSnapshot(userId)
      setContextSnapshot(snapshot)
    } catch (error) {
      console.error('Failed to remove symbol:', error)
      showNotification('error', 'Failed to remove symbol')
    }
  }

  // Handle variable search
  const handleVariableClick = (symbol: string) => {
    if (onVariableSearch) {
      onVariableSearch(symbol)
    }
  }

  // Filter tags
  const filteredTags =
    contextSnapshot?.tags.filter((tag) => {
      const matchesSearch = tag.symbol.toLowerCase().includes(searchQuery.toLowerCase())
      const matchesCategory = selectedCategory === 'all' || tag.category === selectedCategory
      return matchesSearch && matchesCategory && tag.is_valid
    }) || []

  // Get category color
  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      ACTIVITY: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      CONTEXT: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      MOOD: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
      FOCUS: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      GOAL: 'bg-green-500/20 text-green-400 border-green-500/30',
      RELATIONSHIP: 'bg-red-500/20 text-red-400 border-red-500/30',
      TEMPORAL: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
      CREATIVE: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      LEARNING: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
      WELLNESS: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
    }
    return colors[category] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'
  }

  // Get score color
  const getScoreColor = (score: number) => {
    if (score >= 0.8) return 'text-green-400'
    if (score >= 0.6) return 'text-yellow-400'
    if (score >= 0.4) return 'text-orange-400'
    return 'text-red-400'
  }

  if (compact) {
    return (
      <div className={`fixed ${position === 'left' ? 'left-4' : 'right-4'} bottom-20 z-50`}>
        <button
          onClick={() => onClose?.()}
          className="p-4 bg-gradient-to-br from-cyan-500/20 to-teal-500/20 border border-cyan-500/30 rounded-full shadow-lg backdrop-blur-sm hover:scale-110 transition-all"
        >
          <Hash className="w-6 h-6 text-cyan-400" />
          {contextSnapshot && contextSnapshot.tags.length > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-cyan-500 text-white text-xs rounded-full flex items-center justify-center">
              {contextSnapshot.tags.length}
            </span>
          )}
        </button>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full bg-gray-900/50 backdrop-blur-sm border-l border-gray-800">
      {/* Header */}
      <div className="p-4 border-b border-gray-800 bg-gradient-to-br from-cyan-500/10 to-teal-500/10">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-3">
            <Hash className="w-6 h-6 text-cyan-400" />
            <div>
              <h3 className="text-lg font-bold text-white">DΛST</h3>
              <p className="text-xs text-gray-400">Symbol Tracker</p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => setIsAddingSymbol(!isAddingSymbol)}
              className="p-2 hover:bg-gray-800 rounded transition-colors"
              title="Add symbol"
            >
              <Plus className="w-4 h-4 text-cyan-400" />
            </button>

            {onClose && (
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-800 rounded transition-colors"
                title="Close widget"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            )}
          </div>
        </div>

        {/* Status Bar */}
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center space-x-2">
            <div
              className={`w-2 h-2 rounded-full ${
                isConnected ? 'bg-green-500' : 'bg-red-500 animate-pulse'
              }`}
            />
            <span className="text-gray-400">{isConnected ? 'Connected' : 'Disconnected'}</span>
          </div>

          {contextSnapshot && (
            <code className="text-purple-400 font-mono">
              {contextSnapshot.lambda_fingerprint.slice(0, 16)}...
            </code>
          )}
        </div>
      </div>

      {/* Notification */}
      {notification && (
        <div
          className={`p-3 border-b flex items-center space-x-2 ${
            notification.type === 'success'
              ? 'bg-green-500/10 border-green-500/20'
              : 'bg-red-500/10 border-red-500/20'
          }`}
        >
          {notification.type === 'success' ? (
            <Check className="w-4 h-4 text-green-400" />
          ) : (
            <AlertCircle className="w-4 h-4 text-red-400" />
          )}
          <span
            className={`text-xs ${notification.type === 'success' ? 'text-green-400' : 'text-red-400'}`}
          >
            {notification.message}
          </span>
        </div>
      )}

      {/* Connection Error */}
      {connectionError && (
        <div className="p-3 bg-red-500/10 border-b border-red-500/20 flex items-center space-x-2">
          <AlertCircle className="w-4 h-4 text-red-400" />
          <span className="text-xs text-red-400">{connectionError}</span>
        </div>
      )}

      {/* Add Symbol Form */}
      {isAddingSymbol && (
        <div className="p-4 border-b border-gray-800 bg-gray-950/50 space-y-3">
          <input
            type="text"
            value={newSymbol}
            onChange={(e) => setNewSymbol(e.target.value)}
            placeholder="Enter symbol name..."
            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-sm text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAddSymbol()
            }}
          />
          <select
            value={newSymbolCategory}
            onChange={(e) => setNewSymbolCategory(e.target.value as SymbolCategory)}
            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-sm text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
          >
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
          <div className="flex space-x-2">
            <button
              onClick={handleAddSymbol}
              className="flex-1 px-3 py-2 bg-cyan-500 text-white rounded text-sm hover:bg-cyan-600 transition-colors"
            >
              Add Symbol
            </button>
            <button
              onClick={() => {
                setIsAddingSymbol(false)
                setNewSymbol('')
              }}
              className="px-3 py-2 bg-gray-800 text-gray-400 rounded text-sm hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Context Scores */}
      {contextSnapshot && (
        <div className="p-4 border-b border-gray-800 bg-gray-950/50">
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center">
              <div className="flex items-center justify-center space-x-1 mb-1">
                <Activity className="w-3 h-3 text-gray-400" />
                <p className="text-xs text-gray-400">Focus</p>
              </div>
              <p className={`text-lg font-bold ${getScoreColor(contextSnapshot.focus_score)}`}>
                {Math.round(contextSnapshot.focus_score * 100)}%
              </p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center space-x-1 mb-1">
                <Zap className="w-3 h-3 text-gray-400" />
                <p className="text-xs text-gray-400">Coherence</p>
              </div>
              <p className={`text-lg font-bold ${getScoreColor(contextSnapshot.coherence_score)}`}>
                {Math.round(contextSnapshot.coherence_score * 100)}%
              </p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center space-x-1 mb-1">
                <TrendingUp className="w-3 h-3 text-gray-400" />
                <p className="text-xs text-gray-400">Stability</p>
              </div>
              <p className={`text-lg font-bold ${getScoreColor(contextSnapshot.stability_score)}`}>
                {Math.round(contextSnapshot.stability_score * 100)}%
              </p>
            </div>
          </div>

          {contextSnapshot.primary_activity && (
            <div className="mt-3 p-2 bg-gray-800/50 rounded text-center">
              <p className="text-xs text-gray-400">Primary Activity</p>
              <p className="text-sm text-cyan-400 font-medium">{contextSnapshot.primary_activity}</p>
            </div>
          )}
        </div>
      )}

      {/* Search & Filter */}
      <div className="p-4 border-b border-gray-800 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search symbols..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-gray-800/50 border border-gray-700 rounded text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
          />
        </div>

        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value as SymbolCategory | 'all')}
          className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700 rounded text-sm text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
        >
          <option value="all">All Categories</option>
          {categories.map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>
      </div>

      {/* Symbols List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {filteredTags.length === 0 ? (
          <div className="text-center py-8">
            <Hash className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400 text-sm">No symbols found</p>
            <p className="text-gray-500 text-xs mt-1">
              {searchQuery ? 'Try adjusting your search' : 'Add symbols to track your context'}
            </p>
          </div>
        ) : (
          filteredTags.map((tag, index) => (
            <div
              key={index}
              className="p-3 bg-gray-800/50 border border-gray-700 rounded-lg hover:border-cyan-500/50 transition-all group"
            >
              <div className="flex items-start justify-between mb-2">
                <button
                  onClick={() => handleVariableClick(tag.symbol)}
                  className="flex-1 text-left"
                >
                  <p className="text-sm font-medium text-white group-hover:text-cyan-400 transition-colors">
                    {tag.symbol}
                  </p>
                </button>
                <button
                  onClick={() => handleRemoveSymbol(tag.symbol, tag.category)}
                  className="p-1 hover:bg-red-500/20 rounded transition-colors opacity-0 group-hover:opacity-100"
                  title="Remove symbol"
                >
                  <X className="w-3 h-3 text-red-400" />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <span className={`px-2 py-0.5 rounded text-xs border ${getCategoryColor(tag.category)}`}>
                  {tag.category}
                </span>

                <div className="flex items-center space-x-2">
                  <span className="text-xs text-gray-400">
                    {Math.round(tag.confidence * 100)}%
                  </span>
                  <div
                    className="w-12 h-1 bg-gray-700 rounded-full overflow-hidden"
                    title={`Confidence: ${Math.round(tag.confidence * 100)}%`}
                  >
                    <div
                      className={`h-full ${
                        tag.confidence >= 0.8
                          ? 'bg-green-400'
                          : tag.confidence >= 0.5
                          ? 'bg-yellow-400'
                          : 'bg-red-400'
                      }`}
                      style={{ width: `${tag.confidence * 100}%` }}
                    />
                  </div>
                </div>
              </div>

              <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                <span>{tag.source.replace(/_/g, ' ')}</span>
                <code className="text-purple-400 font-mono">{tag.lambda_signature.slice(0, 12)}...</code>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer Stats */}
      <div className="p-3 border-t border-gray-800 bg-gray-950/50">
        <div className="grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="text-lg font-bold text-white">{contextSnapshot?.tags.filter((t) => t.is_valid).length || 0}</p>
            <p className="text-xs text-gray-400">Active</p>
          </div>
          <div>
            <p className="text-lg font-bold text-cyan-400">
              {new Set(contextSnapshot?.tags.map((t) => t.category)).size || 0}
            </p>
            <p className="text-xs text-gray-400">Categories</p>
          </div>
          <div>
            <p className="text-lg font-bold text-purple-400">
              {Math.round(
                ((contextSnapshot?.focus_score || 0) +
                  (contextSnapshot?.coherence_score || 0) +
                  (contextSnapshot?.stability_score || 0)) /
                  3 *
                  100
              )}
              %
            </p>
            <p className="text-xs text-gray-400">Overall</p>
          </div>
        </div>
      </div>
    </div>
  )
}
